import { Directive, EventEmitter, HostListener, inject, OnInit, Output } from '@angular/core'
import { _Retry, loadScript } from '@naturalcycles/js-lib'
import {
  CredentialResponse,
  DecodedCredentialResponse,
  GoogleAuthAPI,
  isChromeBrowser,
  PromptMomentNotification,
} from '@naturalcycles/shared'
import { TranslateService } from '@ngx-translate/core'
import { ErrorComponent } from '@src/app/components/error/error.component.js'
import { Decorate } from '@src/app/core/decorators/decorators.js'
import { ModalService } from '@src/app/core/services/modal.service.js'
import { sentry } from '@src/app/core/util/sentry.util.js'
import { ErrorHandlerType } from '@src/app/shared/typings/enum/error-handler.js'
import { GoogleAuthUser } from '@src/app/shared/typings/interfaces/user-auth.js'
import { LoaderType } from '@src/app/shared/typings/ui.js'
import { env } from '@src/environments/environment.js'
import { jwtDecode } from 'jwt-decode'

declare let google: GoogleAuthAPI

@Directive({
  selector: '[googleAuth]',
  standalone: false,
})
export class GoogleAuthDirective implements OnInit {
  private modalService = inject(ModalService)
  private translateService = inject(TranslateService)
  // TODO [2025-03-24] check if this issue still occurs and switch back to signal if possible https://github.com/angular/angular/issues/19826
  @Output()
  signin = new EventEmitter<GoogleAuthUser>()

  public isChromeBrowser = isChromeBrowser()

  constructor() {
    this.handleCredentialResponse = this.handleCredentialResponse.bind(this)
  }

  async ngOnInit(): Promise<void> {
    await this.loadOneTapLibrary()
    this.configureOneTapClient()
  }

  @HostListener('click')
  public async onClick(): Promise<void> {
    await this.openGoogleSignIn()
  }

  private configureOneTapClient(): void {
    google.accounts.id.initialize({
      client_id: env.googleWebClientId,
      itp_support: true,
      context: 'signin',
      ux_mode: 'popup',
      callback: this.handleCredentialResponse,
    })
    if (this.isChromeBrowser) {
      google.accounts.id.renderButton(document.getElementById('legacy-g-signin')!, {
        width: 400,
      })
    }
  }

  @Decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public handleCredentialResponse(response: CredentialResponse): void {
    const decodedCredential = jwtDecode<DecodedCredentialResponse>(response.credential)

    const user: GoogleAuthUser = {
      id: decodedCredential.sub,
      email: decodedCredential.email,
      name: decodedCredential.name,
      familyName: decodedCredential.family_name,
      givenName: decodedCredential.given_name,
      imageUrl: decodedCredential.picture,
      authentication: {
        idToken: response.credential,
      },
    }
    this.signin.emit(user)
  }

  @_Retry()
  private async loadOneTapLibrary(): Promise<void> {
    await loadScript(`https://accounts.google.com/gsi/client`)
  }

  @Decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  public async openGoogleSignIn(): Promise<void> {
    google.accounts.id.prompt((notification: PromptMomentNotification) => {
      if (notification.isNotDisplayed()) {
        const notDisplayedReason = notification.getNotDisplayedReason()
        let message = 'Error: ' + notDisplayedReason

        switch (notDisplayedReason) {
          case 'suppressed_by_user': {
            sentry.captureMessage(`Google Sign-In: ${notDisplayedReason}`)
            message = this.translateService.instant('auth-google-error-suppressed-by-user')
            break
          }
          default: {
            sentry.captureException(`Google Sign-In: ${notDisplayedReason}`)
          }
        }

        void this.modalService.show({
          component: ErrorComponent,
          modalName: 'GoogleSignPopupNotDisplayed',
          api: {
            error: message,
          },
        })
      }
    })
  }
}
